import {getCSRFToken} from './auth.js';

export enum RequestMethod {
  INFO = 'INFO',
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export interface ApiValidationError extends Error {
  errors: string[];
}

export function isApiValidationError(
  error: unknown
): error is ApiValidationError {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'errors' in error
  ) {
    const possibleError = error as {message: unknown; errors: unknown};

    return (
      typeof possibleError.message === 'string' &&
      Array.isArray(possibleError.errors) &&
      possibleError.errors.every(e => typeof e === 'string')
    );
  }

  return false;
}

/**
 * Simple interface for fetcher parameters.
 */
export interface FetcherParams<K = unknown> {
  method: RequestMethod;
  body?: K | Record<string, unknown> | unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  redirect?: RequestRedirect;
  responseParser?: 'json' | 'formdata' | 'text' | 'blob' | 'url' | null;
}

export function getQueryParameter<T extends string>(key: string): null | T {
  const queryParams = new URLSearchParams(location.search);
  if (!queryParams.has(key)) return null;
  return queryParams.get(key) as T;
}

/**
 * Simple fetch wrap to make native fetch more usable.
 */
export async function fetcher<T, K = unknown>(
  url: string,
  params: FetcherParams<K>
): Promise<T> {
  const headers: Record<string, string> | undefined =
    params.body instanceof FormData
      ? undefined
      : {'Content-Type': 'application/json'};

  if (headers && getCSRFToken()) {
    headers['X-CSRF-Token'] = getCSRFToken();
  }

  const requestData: RequestInit = {
    signal: params.signal,
    method: params.method,
    redirect: params.redirect,
    headers: {
      ...(headers || {}),
      ...params.headers,
    },
  };

  if (params.body) {
    requestData.body =
      params.body instanceof FormData
        ? params.body
        : JSON.stringify(params.body);
  }

  try {
    const res = await fetch(url, requestData);
    let data;
    switch (params.responseParser) {
      case null:
        data = '';
        break;
      case 'formdata':
        data = await res.formData();
        break;
      case 'blob':
        data = await res.blob();
        break;
      case 'text':
        data = await res.text();
        break;
      case 'url':
        data = res.redirected ? res.url : '';
        break;
      default:
        data = await res.json();
        break;
    }

    if (res.ok) {
      return Promise.resolve(data);
    } else {
      return Promise.reject(data);
    }
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
}

export type URLBuilderParams = Record<
  string,
  (string | number)[] | string | number
>;

/**
 * Creates a simple URL given a path string.
 */
export function buildURL(pathStr: string, params: URLBuilderParams = {}): URL {
  const baseURL = `${location.protocol}//${location.host}`;
  const url = new URL(baseURL + pathStr);

  Object.keys(params).forEach(k => {
    const v = params[k];

    if (typeof v === 'string' || typeof v === 'number') {
      url.searchParams.set(k, String(v));
    }

    if (Array.isArray(v)) {
      v.forEach(value => {
        url.searchParams.append(`${k}[]`, String(value));
      });
    }
  });

  return url;
}

/**
 * Get default headers for requests.
 */
export function getDefaultHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCSRFToken(),
  };
}

/**
 * Attaches given params to the current URL and navigates to the new URL.
 *
 * @param params - The params to attach to the current URL.
 * @returns The new URL with the params attached.
 */
export const attachParamsToCurrentUrlAndNavigate = (
  params: URLBuilderParams
): void => {
  const url = buildURL(location.pathname, params);
  location.href = url.toString();
};
